import React from 'react';
import { styled } from '@mui/material';
import { Accordion } from '../Accordion/Accordion';
import type { AccordionProps } from '../Accordion/types';
import { Table, TableProps } from '../Table';

export interface ExpandableTableProps<T = unknown>
  extends Pick<AccordionProps, 'title' | 'chipLabel' | 'expanded' | 'defaultExpanded' | 'onChange'> {
  /**
   * Prefix used to generate stable element ids for accessibility testing.
   */
  idPrefix: string;
  /**
   * Slot to be rendered at the end of the header.
   */
  headerEndslots?: React.ReactNode;
  /**
   * Props forwarded to the underlying `Table` component.
   */
  tableProps: Omit<TableProps<T>, 'isExpandableTableVariant'>;
}

const UnstyledExpandableTable = <T extends Record<string, unknown>>({
  idPrefix,
  title,
  chipLabel,
  headerEndslots,
  tableProps,
  ...accordionProps
}: ExpandableTableProps<T>): JSX.Element => {
  return (
    <Accordion
      idPrefix={idPrefix}
      title={title}
      content={<Table {...tableProps} isExpandableTableVariant={true} />}
      chipLabel={chipLabel}
      expandIconPosition={'left'}
      hasBorder={true}
      chipColor="primary"
      chipPosition="start"
      headerEndslots={headerEndslots}
      {...accordionProps}
    />
  );
};

/**
 * We get a circular dependency error if we do styled(Accordion) so this is a workaround.
 */
export const ExpandableTable = styled(UnstyledExpandableTable)({
  '&.MuiAccordion-root': {
    marginTop: '24px',
    '& .MuiAccordionDetails-root': {
      overflow: 'hidden',
      padding: '0px',
      marginTop: '0px',
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
    },
  },
});
